import React, { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Card,
  Col,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { Link, useParams, useSearchParams } from "react-router";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useGet } from "../../api";
import { PageHeader } from "../../components/ui";
import {
  BrandLink,
  DealLink,
  liveCouponSearch,
  searchCountry,
} from "./kindred-links";
import { ICouponPerformanceRow, TCouponVerdict } from "../../interfaces";

/**
 * One coupon code over time: did it work, when did it stop, and was it the
 * code the shop turned down or our own extension failing to type it in.
 */

const VERDICT_LABEL: Record<TCouponVerdict, { label: string; bg: string }> = {
  failing: { label: "Not working", bg: "danger" },
  rarely: { label: "Rarely works", bg: "warning" },
  working: { label: "Working", bg: "success" },
};

/**
 * Two measures of the same thing (attempts) on one axis, so they stay
 * comparable. Colour carries the series and the legend names it, never colour
 * alone. Grid and axes stay recessive — the lines are the subject.
 */
const SERIES = {
  attempts: { label: "Attempts", colour: "#6c757d" },
  applied: { label: "Worked", colour: "#198754" },
  blocked: { label: "We couldn't enter it", colour: "#fd7e14" },
};

const AXIS = { fontSize: 12, stroke: "#adb5bd" };

interface IDetailResponse {
  code: string;
  brandId: string | null;
  summary: ICouponPerformanceRow | null;
  window: { from: string; to: string };
  errors: Array<{ message: string; occurrences: number; lastSeen: string }>;
  daily: Array<{ day: string; attempts: number; applied: number }>;
  platforms: Array<{ platform: string; attempts: number; applied: number }>;
}

const HOUR = 3600 * 1000;

const RANGE_DAYS: Record<string, number> = {
  "30m": 1 / 48,
  "1h": 1 / 24,
  "4h": 4 / 24,
  "24h": 1,
  "7d": 7,
  "30d": 30,
  "90d": 90,
};

export const CouponDetail: React.FC = () => {
  const { code = "" } = useParams();
  const [params] = useSearchParams();
  const brandId = params.get("brandId") ?? "";
  const partnerId = params.get("partnerId") ?? "";
  const minRate = params.get("minRate") ?? "";
  // Inherited from the list so the verdict here is the one you clicked, then
  // adjustable — the charts are often worth more history than the worklist.
  const [range, setRange] = useState(params.get("range") ?? "24h");
  const days = RANGE_DAYS[range] ?? 1;

  const url = useMemo(() => {
    const p = new URLSearchParams({ code });
    if (brandId) p.set("brandId", brandId);
    if (partnerId) p.set("partnerId", partnerId);
    if (minRate) p.set("minRate", minRate);
    p.set("from", new Date(Date.now() - days * 24 * HOUR).toISOString());
    return `/coupon-performance/detail?${p.toString()}`;
  }, [code, brandId, partnerId, minRate, days]);

  const { data, loading, error } = useGet<IDetailResponse>(url);
  const s = data?.summary ?? null;

  // A day with no attempts is a real zero, not a gap — recharts would
  // otherwise join across it and imply activity that never happened.
  const daily = data?.daily ?? [];

  const successByDay = useMemo(
    () =>
      daily.map((d) => ({
        day: d.day,
        rate: d.attempts > 0 ? Math.round((d.applied / d.attempts) * 100) : 0,
        attempts: d.attempts,
      })),
    [daily]
  );

  return (
    <div className="container-fluid py-3">
      <PageHeader
        title={code}
        description={
          s?.brandName
            ? `${s.brandName}${s.domain ? ` · ${s.domain}` : ""}`
            : "Coupon code"
        }
        actions={
          <>
            <ButtonGroup size="sm">
              {Object.keys(RANGE_DAYS).map((r) => (
                <Button
                  key={r}
                  variant={range === r ? "primary" : "outline-secondary"}
                  onClick={() => setRange(r)}
                  title={`Show the last ${r}`}
                >
                  {r}
                </Button>
              ))}
            </ButtonGroup>
            {s && (
              <Button
                as={Link as any}
                to={liveCouponSearch(
                  s.code,
                  s.brandId,
                  searchCountry(
                    s.countries.map((c) => c.country),
                    s.markets
                  )
                )}
                target="_blank"
                rel="noreferrer"
                size="sm"
                variant="outline-primary"
                title="Open this code in Kindred Live coupons, filtered to its brand and country"
              >
                <i className="bi bi-search me-1" />
                Find in Kindred Live
              </Button>
            )}
            <Button
              as={Link as any}
              to="/coupon-performance"
              size="sm"
              variant="outline-secondary"
            >
              Back to all coupons
            </Button>
          </>
        }
      />

      {error && (
        <Alert variant="danger" className="small">
          Could not load this coupon&apos;s history.
        </Alert>
      )}
      {loading && <Spinner />}

      {data && (
        <>
          {s && (
            <div className="small text-muted mb-3 font-monospace">
              Brand ID: {s.brandId ?? "—"}
              <BrandLink brandId={s.brandId} /> · Deal:{" "}
              <DealLink campaignId={s.campaignId} />
              {s.countries.length > 0 && (
                <>
                  {" · "}
                  {s.countries
                    .map((c) => `${c.country} ${c.attempts}`)
                    .join(", ")}
                </>
              )}
              {s.partners.length > 0 && (
                <>
                  {" · "}
                  {s.partners
                    .map((p) => `${p.name ?? p.partnerId} ${p.attempts}`)
                    .join(", ")}
                </>
              )}
            </div>
          )}
          {s?.checkoutUrl && (
            <Alert variant="light" className="small border py-2">
              Last seen at{" "}
              <a href={s.checkoutUrl} target="_blank" rel="noreferrer">
                {s.checkoutUrl}
              </a>{" "}
              — the checkout page a real attempt reported.
            </Alert>
          )}
          {s && (
            <Row className="g-2 mb-3">
              <Col md={4}>
                <Card className="h-100">
                  <Card.Body className="py-2 px-3">
                    <div className="small text-muted">Status</div>
                    <Badge
                      bg={VERDICT_LABEL[s.verdict].bg}
                      text={s.verdict === "rarely" ? "dark" : undefined}
                      className="fs-6"
                    >
                      {VERDICT_LABEL[s.verdict].label}
                    </Badge>
                    <div className="small text-muted mt-1">
                      {s.isLive
                        ? "still shown to users"
                        : "no longer shown to users"}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="h-100">
                  <Card.Body className="py-2 px-3">
                    <div className="small text-muted">Attempts</div>
                    <div className="fs-4 fw-bold">
                      {s.attempts.toLocaleString()}
                    </div>
                    <div className="small text-muted">
                      across {s.checkouts.toLocaleString()} checkouts
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="h-100">
                  <Card.Body className="py-2 px-3">
                    <div className="small text-muted">Worked</div>
                    <div className="fs-4 fw-bold">
                      {s.applied.toLocaleString()}
                    </div>
                    <div className="small text-muted">
                      {s.applyRate == null
                        ? "—"
                        : `${(s.applyRate * 100).toFixed(1)}% of attempts`}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          <Card className="mb-3">
            <Card.Body>
              <h6 className="mb-1">Completions over time</h6>
              <p className="small text-muted">
                How often the code was tried, how often the shop actually got to
                check it, and how often it worked. A gap between
                &ldquo;attempts&rdquo; and &ldquo;shop checked it&rdquo; is our
                extension failing to find the discount box.
              </p>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={daily}
                  margin={{ top: 4, right: 16, bottom: 0, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                  <XAxis dataKey="day" tick={AXIS} />
                  <YAxis tick={AXIS} allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="attempts"
                    name={SERIES.attempts.label}
                    stroke={SERIES.attempts.colour}
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="applied"
                    name={SERIES.applied.label}
                    stroke={SERIES.applied.colour}
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>

          <Row className="g-3 mb-3">
            <Col lg={7}>
              <Card className="h-100">
                <Card.Body>
                  <h6 className="mb-1">Success rate</h6>
                  <p className="small text-muted">
                    The share of attempts that worked. A cliff to zero is the
                    day the code stopped.
                  </p>
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart
                      data={successByDay}
                      margin={{ top: 4, right: 16, bottom: 0, left: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                      <XAxis dataKey="day" tick={AXIS} />
                      <YAxis
                        tick={AXIS}
                        domain={[0, 100]}
                        unit="%"
                        allowDecimals={false}
                      />
                      <Tooltip formatter={(v: any) => `${v}%`} />
                      <Line
                        type="monotone"
                        dataKey="rate"
                        name="Worked"
                        stroke={SERIES.applied.colour}
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={5}>
              <Card className="h-100">
                <Card.Body>
                  <h6 className="mb-1">By device</h6>
                  <p className="small text-muted">
                    A code that only fails on one platform is usually our
                    extension there, not the coupon.
                  </p>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart
                      data={data.platforms}
                      margin={{ top: 4, right: 16, bottom: 0, left: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                      <XAxis dataKey="platform" tick={AXIS} />
                      <YAxis tick={AXIS} allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      <Bar
                        dataKey="attempts"
                        name={SERIES.attempts.label}
                        fill={SERIES.attempts.colour}
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar
                        dataKey="applied"
                        name={SERIES.applied.label}
                        fill={SERIES.applied.colour}
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card>
            <Card.Body>
              <h6 className="mb-1">What the shop said</h6>
              <p className="small text-muted">
                Rejection text exactly as the site returned it — the evidence to
                paste into a retest ticket.
              </p>
              <Table size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Message</th>
                    <th className="text-end">Times</th>
                    <th>Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {data.errors.map((e) => (
                    <tr key={e.message}>
                      <td className="small">{e.message.trim() || "(blank)"}</td>
                      <td className="text-end">
                        {e.occurrences.toLocaleString()}
                      </td>
                      <td className="small text-muted text-nowrap">
                        {new Date(e.lastSeen).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                  {!data.errors.length && (
                    <tr>
                      <td colSpan={3} className="text-muted">
                        No rejection text recorded.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </>
      )}
    </div>
  );
};
